import { datadogRum } from '@datadog/browser-rum';
import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { client } from '../__generated__/client.gen';

export interface ClientConfig {
  baseUrl: string;
  getToken: () => string | null | undefined;
  getImpersonatedUserId: () => string | null | undefined;
}

const TIMEOUT = 30000;

let configuredAxiosInstance: AxiosInstance | null = null;

const responseInterceptors: Array<(response: AxiosResponse) => AxiosResponse> = [];

export function addResponseInterceptor(interceptor: (response: AxiosResponse) => AxiosResponse): void {
  if (!responseInterceptors.includes(interceptor)) {
    responseInterceptors.push(interceptor);
  }

  if (configuredAxiosInstance) {
    configuredAxiosInstance.interceptors.response.use(interceptor);
  }
}

export function configureApiV2Client(config: ClientConfig): void {
  const axiosInstance = axios.create({
    baseURL: config.baseUrl,
    timeout: TIMEOUT,
    withCredentials: true,
  });

  axiosInstance.interceptors.request.use((requestConfig) => {
    const token = config.getToken();
    if (token) {
      requestConfig.headers.Authorization = `Bearer ${token}`;
    }

    const impersonatedUserId = config.getImpersonatedUserId();
    if (impersonatedUserId) {
      requestConfig.params = {
        ...(requestConfig.params || {}),
        impersonatedUserId,
      };
    }

    return requestConfig;
  });

  axiosInstance.interceptors.response.use(
    (response) => {
      return responseInterceptors.reduce((res, interceptor) => interceptor(res), response);
    },
    async (error) => {
      if (error.response?.status === 400) {
        datadogRum.addError(error);
      }
      error.message = `${error.config?.method?.toUpperCase()} ${error.config?.url} - ${error.message}`;
      return Promise.reject(error);
    },
  );

  configuredAxiosInstance = axiosInstance;

  client.setConfig({
    axios: axiosInstance,
  });
}

export function getAxiosInstance(): AxiosInstance {
  if (!configuredAxiosInstance) {
    throw new Error('API V2 client not configured. Call configureApiV2Client() first.');
  }
  return configuredAxiosInstance;
}
